import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../../redux/store';
import { fetchPaymentById } from '../../redux/paymentSlice';
import { approvePaymentRequest, rejectPaymentRequest } from '../../redux/adminPaymentSlice';
import { useNotifications } from '../../hooks/useNotifications';
import { paymentsApi } from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import AdminSidebar from '../../components/sidebar/AdminSidebar';
import StatusBadge from '../../components/StatusBadge';
import DocumentUploadInput from '../../components/DocumentUploadInput';
import ApprovalWorkflowDrawer from '../../components/dashboard/ApprovalWorkflowDrawer';
import './AdminActionPage.css';


const AdminActionPage: React.FC = () => {
  const { paymentId } = useParams<{ paymentId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const notifications = useNotifications();
  const { currentPayment, loading, error } = useSelector((state: RootState) => state.payment);

  // const [actionReason, setActionReason] = useState<string>('');
  const [selectedDocument, setSelectedDocument] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<string>('receipt');
  const [uploadingDocument, setUploadingDocument] = useState<boolean>(false);
  const [isApprovalDrawerOpen, setIsApprovalDrawerOpen] = useState(false);
  // const [reapErrorModal, setReapErrorModal] = useState<{ isOpen: boolean; errorMessage: string; paymentId: string }>({ isOpen: false, errorMessage: '', paymentId: '' });

  useEffect(() => {
    if (paymentId) {
      dispatch(fetchPaymentById(paymentId));
    }
  }, [dispatch, paymentId]);

  const handleApprove = async () => {
    if (!paymentId) return;
    try {
      const result = await dispatch(approvePaymentRequest(paymentId)).unwrap();

      // Check if Reap API call failed
      if (result.reapStatus === 'failed') {
        notifications.warning('Payment approved but Reap API submission failed.');
        // Show error details in modal
        // setReapErrorModal({
        //   isOpen: true,
        //   errorMessage: result.reapError || 'Unknown error occurred during Reap submission',
        //   paymentId: paymentId
        // });
      } else {
        notifications.success('Payment request approved successfully!');
      }

      setIsApprovalDrawerOpen(false);
      // Refresh payment data to show updated status
      dispatch(fetchPaymentById(paymentId));
    } catch (error) {
      console.error('Failed to approve payment request:', error);
      notifications.error('Failed to approve payment request. Please try again.');
    }
  };

  const handleReject = async (rejectionReason: string) => {
    if (!paymentId) return;

    try {
      await dispatch(rejectPaymentRequest({ paymentId, rejectionReason })).unwrap();
      notifications.success('Payment request rejected.');
      setIsApprovalDrawerOpen(false);
      navigate('/admin/admin-payments');
    } catch (error) {
      console.error('Failed to reject payment request:', error);
      notifications.error('Failed to reject payment request. Please try again.');
    }
  };

  const formatCurrency = (amount: number, currencyCode: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const handleViewInvoice = async () => {
    if (!paymentId) return;
    try {
      const { invoiceUrl } = await paymentsApi.getPaymentInvoiceUrl(paymentId);
      window.open(invoiceUrl, '_blank');
    } catch (error) {
      console.error('Failed to open invoice:', error);
      notifications.error('Failed to open invoice. Please try again.');
    }
  };

  const handleDownloadInvoice = async () => {
    if (!paymentId) return;
    try {
      const { invoiceUrl, fileName } = await paymentsApi.getPaymentInvoiceUrl(paymentId);
      const link = document.createElement('a');
      link.href = invoiceUrl;
      link.download = fileName || `invoice-${paymentId}.pdf`;
      link.target = '_blank';
      link.rel = 'noreferrer';
      link.click();
    } catch (error) {
      console.error('Failed to download invoice:', error);
      notifications.error('Failed to download invoice. Please try again.');
    }
  };

  const handleDownloadPaymentReceipt = async () => {
    if (!paymentId) return;
    try {
      const blob = await paymentsApi.downloadPaymentReceipt(paymentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `payment-receipt-${paymentId}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download payment receipt:', error);
      notifications.error('Failed to download receipt. Please try again.');
    }
  };

  const handleDocumentUpload = async () => {
    if (!selectedDocument || !paymentId) return;

    setUploadingDocument(true);
    try {
      const { uploadUrl } = await paymentsApi.uploadPaymentDocuments(paymentId, {
        documentType,
        fileName: selectedDocument.name,
        fileSize: selectedDocument.size,
        mimeType: selectedDocument.type,
      });

      await fetch(uploadUrl, {
        method: 'PUT',
        body: selectedDocument,
        headers: {
          'Content-Type': selectedDocument.type,
        },
      });

      notifications.success('Document uploaded successfully!');
      setSelectedDocument(null);
      // Refresh payment data to show new document
      dispatch(fetchPaymentById(paymentId));
    } catch (error) {
      console.error('Failed to upload document:', error);
      notifications.error('Failed to upload document. Please try again.');
    } finally {
      setUploadingDocument(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout sidebar={<AdminSidebar />}>
        <div className="loading">
          <p>Loading payment details...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !currentPayment) {
    return (
      <DashboardLayout sidebar={<AdminSidebar />}>
        <div className="error">
          <p>Failed to load payment details. {error}</p>
          <button onClick={() => navigate('/admin/admin-payments')}>Back to Payments</button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout sidebar={<AdminSidebar />}>
      <div className="admin-action-page">
        <div className="page-header">
          <h2>Payment Action</h2>
          <p>Review and take action on payment request</p>
        </div>

        <div className="payment-details">
          <div className="detail-section">
            <h3>Payment Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Recipient Company:</label>
                <span>{currentPayment.recipientCompany}</span>
              </div>
              <div className="detail-item">
                <label>Account Number:</label>
                <span>{currentPayment.accountNumber}</span>
              </div>
              <div className="detail-item">
                <label>Bank:</label>
                <span>{currentPayment.recipientBank}</span>
              </div>
              <div className="detail-item">
                <label>Foreign Amount:</label>
                <span>{formatCurrency(currentPayment.foreignAmount, currentPayment.foreignCurrency)}</span>
              </div>
              <div className="detail-item">
                <label>Local Amount:</label>
                <span>{formatCurrency(currentPayment.localAmount, 'NGN')}</span>
              </div>
              <div className="detail-item">
                <label>Exchange Rate:</label>
                <span>{currentPayment.exchangeRate}</span>
              </div>
              <div className="detail-item">
                <label>Status:</label>
                <StatusBadge status={currentPayment.status} type="payment" />
              </div>
              <div className="detail-item">
                <label>Submitted:</label>
                <span>{currentPayment.createdAt ? new Date(currentPayment.createdAt).toLocaleDateString() : 'N/A'}</span>
              </div>
            </div>
          </div>

          {currentPayment.invoiceUrl && (
            <div className="detail-section">
              <h3>Invoice & Documents</h3>
              <div className="action-buttons">
                <button
                  onClick={handleViewInvoice}
                  className="view-invoice-btn"
                >
                  View Invoice
                </button>
                <button
                  onClick={handleDownloadInvoice}
                  className="view-invoice-btn"
                >
                  Download Invoice
                </button>
                {(currentPayment.status === 'completed' || currentPayment.status === 'failed' || currentPayment.status === 'processing') && (
                  <button
                    onClick={handleDownloadPaymentReceipt}
                    className="view-invoice-btn"
                    style={{ backgroundColor: '#2b2a2a', color: 'white' }}
                  >
                    Download Transaction Receipt
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="detail-section">
            <h3>Upload Additional Documents</h3>
            <div className="document-upload-section">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="documentType">Document Type</label>
                  <select
                    id="documentType"
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                  >
                    <option value="receipt">Payment Receipt</option>
                    <option value="confirmation">Bank Confirmation</option>
                    <option value="statement">Bank Statement</option>
                    <option value="other">Other Document</option>
                  </select>
                </div>
              </div>

              <DocumentUploadInput
                label="Select Document"
                onFileSelect={setSelectedDocument}
                acceptedTypes=".pdf,.jpg,.jpeg,.png"
                maxSize={5}
              />

              <div className="upload-actions">
                <button
                  onClick={handleDocumentUpload}
                  disabled={!selectedDocument || uploadingDocument}
                  className="upload-btn"
                >
                  {uploadingDocument ? 'Uploading...' : 'Upload Document'}
                </button>
              </div>
            </div>
          </div>

          {(currentPayment.reapStatus === 'failed' || currentPayment.reapErrorMessage) && (
            <div className="detail-section error-section" style={{ backgroundColor: '#fff3f3', borderLeft: '4px solid #dc3545' }}>
              <h3 style={{ color: '#dc3545' }}>Comprehensive Transaction Error</h3>
              <div className="error-alert">
                <>
                  <p><strong>Error Reason:</strong> {currentPayment.reapErrorMessage || 'Transaction failed or rejected by provider.'}</p>
                  {currentPayment.reapRawResponse && (
                    <div className="raw-response" style={{ marginTop: '15px' }}>
                      <strong>Raw Response Data (from Reap):</strong>
                      <pre style={{ backgroundColor: '#212529', color: '#f8f9fa', padding: '10px', borderRadius: '4px', overflowX: 'auto', fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(currentPayment.reapRawResponse, null, 2)}
                      </pre>
                    </div>
                  )}
                </>
              </div>
            </div>
          )}

          {currentPayment.status === 'pending_admin_approval' && (
            <div className="action-section">
              <h3>Take Action</h3>
              <div className="action-buttons">
                <button
                  onClick={() => setIsApprovalDrawerOpen(true)}
                  className="approve-btn"
                >
                  Open Approval Drawer
                </button>
              </div>
            </div>
          )}

          {currentPayment.status !== 'pending_admin_approval' && (
            <div className="action-section">
              <h3>Action Taken</h3>
              <p>This payment request has been {currentPayment.status.replace('_', ' ')}.</p>
            </div>
          )}
        </div>

        <div className="back-button">
          <button onClick={() => navigate('/admin/admin-payments')}>Back to All Payments</button>
        </div>

        <ApprovalWorkflowDrawer
          isOpen={isApprovalDrawerOpen}
          title="Single Payment Decision"
          subtitle="Finalize this request and attach reason notes for audit."
          onClose={() => setIsApprovalDrawerOpen(false)}
          contextItems={[
            { label: 'Recipient', value: currentPayment.recipientCompany },
            { label: 'Bank', value: currentPayment.recipientBank },
            { label: 'Account Number', value: currentPayment.accountNumber },
            { label: 'Amount', value: formatCurrency(currentPayment.localAmount, 'NGN') },
          ]}
          onApprove={handleApprove}
          onReject={handleReject}
          rejectionLabel="Decision Notes"
          rejectionPlaceholder="Document why this payment failed compliance or business checks."
        />
      </div>
    </DashboardLayout>
  );
};

export default AdminActionPage;
